import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BuildChip, Wordmark } from '../Landing';
import { type ActorPermissions, clearSession, getPermissions, refreshPermissions } from '../api';
import { LangSwitch } from '../components/LangSwitch';
import { NotificationBell } from '../components/NotificationBell';
import { ShortcutsProvider } from '../components/Shortcuts';
import { BottomNav, DesktopTabs, type Tab } from '../components/ui/AppTabs';
import { t } from '../i18n';

export function StaffLayout() {
  const nav = useNavigate();
  function out() {
    // Staff don't have email; landing on `/` would show the owner-only
    // options ("Start a household", email sign-in) and ask them for an
    // address they don't have. Send them straight to their own sign-in
    // form (username + household code + password).
    clearSession();
    nav('/staff/login');
  }
  // Everyone gets their own work, their history, their pay and the house
  // rules. Someone the owner has trusted with more gets the screens for it —
  // until now those permissions granted abilities with nowhere to use them.
  // Refreshed on mount so a permission the owner granted (or revoked) a minute
  // ago is what the More page reads. The rows themselves are decided there.
  const [perms, setPerms] = useState<ActorPermissions>(getPermissions);
  useEffect(() => {
    void refreshPermissions().then(setPerms);
  }, []);

  // Four along the bottom, never more. A housekeeper opens three of these all
  // day — her work, what she has done, what she is paid — and everything else
  // perhaps once a week, so everything else is one tap behind "More". A person
  // the owner has trusted with permissions used to get up to seven tabs on a
  // 390px screen, each one 55 pixels wide with a truncated label.
  const tabs: Tab[] = [
    { to: '/today', label: t('Today'), icon: 'tasks' },
    { to: '/history', label: t('History'), icon: 'routines' },
    { to: '/pay', label: t('My pay'), icon: 'money' },
    { to: '/more', label: t('More'), icon: 'memory' },
  ];
  // The personal screens stay phone-width even on a laptop — staff use phones,
  // and a card that suddenly spans a desktop reads as a different app. The
  // management screens are the exception: they were built for the owner's wide
  // layout, and squeezing the staff list into a phone column on a desktop
  // wrapped the heading over three lines and truncated every name to two
  // letters. On a phone both widths are the viewport, so nothing changes there.
  const wide = ['/tasks', '/staff', '/money'].includes(useLocation().pathname);
  const column = wide ? 'max-w-5xl' : 'max-w-md';

  return (
    <div className="app-shell app-shell-with-bottom-nav">
      {/* Desktop only, the same reason the owner's is. On a 390px phone this
          bar was a truncated wordmark, a build chip, a bell, a language
          dropdown and two words of admin above a screen that opens by saying
          good morning to her by name. His screens carry their own heading;
          language and sign-out live on the profile screen. It stays on a
          laptop because the bottom-nav is `sm:hidden`. */}
      <header className="app-header hidden sm:block">
        <div
          className={`mx-auto flex w-full ${column} items-center justify-between gap-3 px-4 py-3`}
        >
          <div className="flex items-center gap-2">
            <Link to="/today" aria-label={t('Home')} className="touch">
              <Wordmark compact className="!flex-row !gap-2" />
            </Link>
            <BuildChip />
          </div>
          <div className="flex items-center gap-1">
            <NavLink
              to="/search"
              aria-label={t('Search')}
              className="touch inline-flex items-center justify-center rounded-md px-2 text-foreground/80"
            >
              <Icon icon="ph:magnifying-glass-bold" className="text-lg" />
            </NavLink>
            <NotificationBell />
            <LangSwitch />
            <NavLink
              to="/profile"
              className="touch inline-flex items-center justify-center rounded-md px-3 text-sm text-foreground/80"
            >
              {t('Profile')}
            </NavLink>
            <button
              type="button"
              onClick={out}
              className="touch inline-flex items-center justify-center rounded-md px-3 text-sm text-muted-foreground"
            >
              {t('Sign out')}
            </button>
          </div>
        </div>
      </header>

      {/* Desktop tab row. The bottom-nav below is `sm:hidden`, so without this
          a staff member on a laptop would have no way off /today. */}
      <DesktopTabs tabs={tabs} className={`mx-auto w-full ${column} px-4`} />

      <main className={`mx-auto w-full ${column} flex-1 px-4 py-6`}>
        <Outlet />
      </main>

      {/* The same round + the owner has, for the same reason and only for the
          person who is allowed to use it. The server has always accepted a task
          created by anyone with `assign_work` — it checks the permission, not
          the role — but the house manager had no button anywhere in her app, so
          the ability existed with no way to reach it and she went on asking the
          owner to type it for her. Four tabs stay four tabs. */}
      <BottomNav
        tabs={tabs}
        {...(perms.assign_work
          ? { center: { label: t('New task'), onClick: () => nav('/tasks?new=1') } }
          : {})}
      />

      <ShortcutsProvider />
    </div>
  );
}
